import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import styled from '@emotion/styled';
import { runInAction } from 'mobx';
import { observer } from 'mobx-react';
import { useStore } from '@stores/RootStore';
import { fonts } from '@theme/fontsCustomer';
import { ConditionConfig } from './ConditionConfig';

export const ConditionSelect = observer(() => {
  const { t } = useTranslation();
  const { workflowDetailStore } = useStore();
  const [prevNodeId, setPrevNodeId] = useState<string | null>(null);
  const [showConditionConfig, setShowConditionConfig] = useState<boolean>(false);
  const isEditTable = workflowDetailStore.checkEditable();

  // 현재 노드 정보를 MobX로 자동 감지
  const currentNode = workflowDetailStore.findNode(workflowDetailStore.curNodeId ?? '');
  const currentNodeId = workflowDetailStore.curNodeId ?? '';
  const currentActionModel = currentNode?.data.actionModel;

  // 노드가 변경되면 Config 표시 여부 결정
  useEffect(() => {
    // 노드 ID가 변경되지 않았으면 아무것도 하지 않음
    if (prevNodeId === currentNodeId) {
      return;
    }

    // 현재 노드 ID 저장
    setPrevNodeId(currentNodeId);

    if (!currentNode) {
      setShowConditionConfig(false);
      return;
    }

    // 조건이 설정되지 않은 새 노드인 경우
    if (!currentActionModel || currentActionModel.type !== 'IF_ACTION') {
      // IF_ACTION으로 초기화
      runInAction(() => {
        if (currentNode.data.actionModel) {
          currentNode.data.actionModel.type = 'IF_ACTION';
          currentNode.data.actionModel.actionName = t('Workflow_Create_IfAction');
          currentNode.data.actionModel.actionMethod = '';
          currentNode.data.actionModel.actionArguments = [
            {
              argumentName: 'expr',
              expression: '',
              expressionType: 'SIMPLE',
            },
          ];
        }
      });
      setShowConditionConfig(true);
      return;
    }

    // 이미 IF_ACTION으로 설정된 노드인 경우
    setShowConditionConfig(true);
    // MobX observer가 currentNode와 currentActionModel 변경을 자동 감지하므로
    // 의존성 배열에서 제외 (불필요한 리렌더링 방지)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentNodeId, prevNodeId, t]);

  return (
    <Container>
      <Title>{t('Workflow_Create_78')}</Title>
      <ConditionContainer>
        <ToolbarName>{t('Workflow_Create_IfAction')}</ToolbarName>
        {isEditTable && <ConditionDescription> {t('Workflow_Create_IfActionDesc')}</ConditionDescription>}

        {/* 조건 설정 화면 */}
        {showConditionConfig && <ConditionConfig />}
      </ConditionContainer>
    </Container>
  );
});

const Container = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
  background: inherit;
`;

const Title = styled.strong`
  ${fonts.Headline6};
  width: 100%;
  padding: 32px;
  border-bottom: 1px solid ${({ theme: { colors } }) => colors['border-gray-lighter']};
`;

const ConditionContainer = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  padding: 40px 32px;
`;

const ToolbarName = styled.strong`
  display: flex;
  flex-direction: row;
  width: 100%;
  ${fonts.Headline7};
  color: ${({ theme: { colors } }) => colors['text-gray-main']};
`;

const ConditionDescription = styled.p`
  ${fonts.Body2};
  color: ${({ theme: { colors } }) => colors['text-gray-sub-dark']};
  margin: 4px 0 28px;
`;
